/**
 * /docs/getting-started — Tenant setup checklist for the firm administrator.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { LegalLayout } from '../../components/layouts/legal-layout';

export const Route = createFileRoute('/docs/getting-started')({
  component: GettingStartedPage,
});

const TOC = [
  { anchor: 'before-you-start', label: 'Before you start' },
  { anchor: 'day-one',          label: 'Day one: provision the tenant' },
  { anchor: 'day-two',          label: 'Day two: invite administrators' },
  { anchor: 'day-three',        label: 'Day three: onboard the first trust' },
  { anchor: 'after',            label: 'After the first week' },
];

function GettingStartedPage() {
  useEffect(() => {
    document.title = 'Getting started · Elreign documentation';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'Tenant setup checklist for South African fiduciary firms commissioning Elreign. From provisioning the subdomain through to the first trust onboarded.',
    );
  }, []);

  return (
    <LegalLayout heading="Getting started." lastUpdated="17 May 2026" toc={TOC}>
      <p>
        This guide is for the partner or principal administrator commissioning Elreign for a
        fiduciary firm. It assumes you have a signed contract and a tenant subdomain reserved.
        Allow three working days to reach the first signed resolution.
      </p>

      <h2 id="before-you-start">Before you start</h2>
      <p>You will need the following to hand:</p>
      <ul>
        <li>A list of the administrators who will use the platform, with their email addresses and intended roles.</li>
        <li>Your firm logo as an SVG, and your primary brand colour as a hex value.</li>
        <li>The name and contact details of the partner or director who is the designated Information Officer under POPIA s55.</li>
        <li>FICA paperwork for your firm: Money Laundering Reporting Officer designation, latest risk management and compliance programme.</li>
        <li>One trust deed to onboard as the test trust, with the consent of its trustees.</li>
      </ul>

      <h2 id="day-one">Day one: provision the tenant</h2>
      <p>
        We provision the tenant subdomain at <strong>firm.elreign.io</strong> from our side. You
        receive an email at the principal administrator address with a one-time link to activate
        the principal account. Activation includes MFA enrolment (TOTP or platform biometric),
        upload of the firm logo, and selection of the primary brand colour.
      </p>
      <p>
        Once activated, the principal administrator sees an empty tenant with a single
        notification: a guided walkthrough of the Information Officer designation flow. Complete
        this designation before any other administrator is invited.
      </p>

      <h2 id="day-two">Day two: invite administrators</h2>
      <p>
        Open <strong>Settings · Team</strong> and invite each administrator by email. Roles are:
      </p>
      <ul>
        <li><strong>Principal administrator</strong>: full access, can invite, can change tenant settings, can designate the Information Officer.</li>
        <li><strong>Senior administrator</strong>: full access to trusts they manage, can onboard new trusts.</li>
        <li><strong>Administrator</strong>: full access to trusts assigned to them.</li>
        <li><strong>Read-only</strong>: typically for auditors or witnessing consultants.</li>
      </ul>
      <p>
        Each invitee receives an email with a one-time activation link valid for seven days.
        First login forces MFA enrolment.
      </p>

      <h2 id="day-three">Day three: onboard the first trust</h2>
      <p>
        From the dashboard, choose <strong>Onboard trust</strong>. Drop the client&apos;s document
        folder (trust deed, trustee and beneficiary identification, and the opening asset
        schedule) and the platform reads every document, reconciles what it finds, and slots it
        into an editable review dossier. Work through the dossier to confirm deed particulars,
        trustee panel, beneficiary register, and the initial asset schedule before committing.
        Allow roughly 90 minutes for the first trust, and 45 minutes for each subsequent one as the
        team becomes familiar with the structure.
      </p>
      <p>
        When the trust is provisioned, draft the first resolution from the trust&apos;s page.
        Choose a simple resolution for the test trust (typically a routine acknowledgement of
        annual trustee duties) and walk through the signing flow with at least one trustee
        physically present in the room. The full flow from draft to chain-sealed signature should
        take under five minutes.
      </p>

      <h2 id="after">After the first week</h2>
      <p>
        With one trust onboarded and one resolution signed, the platform is operational. Migrate
        the rest of your trust register at a pace that suits the team. Most firms in our beta
        cohort complete migration in three to six weeks of regular work, depending on register
        size and the state of the source records.
      </p>
      <p>
        For migration support, write to{' '}
        <a href="mailto:onboarding@elreign.io">onboarding@elreign.io</a>. We provide a senior
        onboarding engineer on site for the first week and remote for the next two, included in
        the activation fee.
      </p>

      <p style={{ marginTop: 32 }}>
        <Link to="/docs">Back to documentation hub →</Link>
      </p>
    </LegalLayout>
  );
}
